import React, { useEffect, useState } from 'react';
import { InfoMessage } from 'components';
import {
  Button,
  Title,
  Tabs,
  Flex,
  Loader,
  Skeleton,
  Heading,
  Divider,
  Colors,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import {
  Rule,
  Milestone,
  RunStatus,
  _RuleConnection,
  useGetCollectionRunReportQuery,
  useGetCollectionDetailsReportQuery,
} from 'types/dt-graphql-apollo-v2';
import { useUrl } from 'hooks/useUrls';
import { RuleRunStatus } from 'types/graphql-apollo-v2';
import { CollectionStatistics } from './components/CollectionStatistics';
import { CollectionRulesTable } from './components/CollectionRulesTable';
import { ViewReportRulePage } from './ReportRulePage/ViewReportRulePage';

type TabKeys = 'rules' | 'items';

export const ViewCollectionReportPage = () => {
  const collectionReportId = useUrl.CollectionReportId();

  const [activeTabKey, setActiveTabKey] = useState<TabKeys>('rules');
  const [selectedRule, setSelectedRule] = useState<Rule | null>(null);

  const {
    data: collectionRunsData,
    loading: collectionRunsLoading,
    error: collectionRunsError,
    startPolling: startPollingRuns,
    stopPolling: stopPollingRuns,
  } = useGetCollectionRunReportQuery({
    variables: {
      collectionReportId,
    },
  });

  const allRuleRuns =
    (collectionRunsData?.listRuleRunStatus?.items as RuleRunStatus[]) ?? [];

  const collectionId =
    allRuleRuns[0]?.rule?.milestones?.items[0]?.externalId ?? ' ';

  const hasIncompleteRun = allRuleRuns.some(
    (run) => run?.status === RunStatus.InProgress
  );

  const {
    data: collectionDetailsData,
    loading: collectionDetailsLoading,
    error: collectionDetailsError,
  } = useGetCollectionDetailsReportQuery({
    variables: {
      collectionId,
    },
  });

  const collection = collectionDetailsData?.listMilestone
    ?.items[0] as Milestone;

  /* Handles polling of rules that have runs In Progress. 
    Also keeps pulling data until we get the latest filtered result. */
  useEffect(() => {
    if (!collectionRunsData) return;

    if (hasIncompleteRun) {
      startPollingRuns(5000);
    } else {
      stopPollingRuns();
    }
  }, [hasIncompleteRun]);

  // This will be the base for the collection report state when filtering
  const collectionReport = collection;

  const schemaCollectionRefId = collectionReport?.schemaCollection?.refId || '';

  const loading = collectionRunsLoading;

  /** Render details (name, schema name, statistics) about the collection. */
  const renderCollectionDetails = () => {
    if (collectionDetailsLoading) {
      return <Skeleton.List lines={1} />;
    }
    if (collectionDetailsError) {
      return (
        <InfoMessage
          title="Something went wrong while fetching the collection details"
          type="Error"
          size={0}
        >
          {collectionDetailsError.message}
        </InfoMessage>
      );
    }

    return (
      <StyledCollectionSummary>
        <Heading level={4} className="collection-name">
          {collection?.name}
        </Heading>
        <Heading level={4}>{collection?.schemaCollection?.name}</Heading>
        <CollectionStatistics collection={collection} />
      </StyledCollectionSummary>
    );
  };

  /** If no rule is selected, then render the runs table of the collection. Otherwise, details about the selected rule will be shwon.  */
  const renderPageContent = () => {
    if (collectionRunsError?.message) {
      return (
        <InfoMessage
          type="Error"
          title="Something went wrong while fetching the collection report"
        >
          {collectionRunsError.message}
        </InfoMessage>
      );
    }

    if (loading) {
      return <Loader infoTitle="Loading rules" darkMode={false} />;
    }

    if (!collectionRunsData) {
      return (
        <InfoMessage type="Search" title="Data Quality Explorer is empty">
          <span>
            No collection report with the id <b>{collectionReportId}</b> was
            found.
          </span>
        </InfoMessage>
      );
    }

    // If a rule is presed, change the page to ViewReportRulePage
    if (selectedRule) {
      return (
        <ViewReportRulePage
          ruleId={selectedRule.externalId}
          schemaCollectionRefId={schemaCollectionRefId}
        />
      );
    }

    return (
      <StyledTabContainer>
        <CollectionRulesTable
          collection={collection}
          ruleRuns={allRuleRuns}
          selectRule={(rule) => setSelectedRule(rule)}
        />
      </StyledTabContainer>
    );
  };

  return (
    <StyledPage direction="column">
      <StyledRowHeader
        style={{
          padding: selectedRule ? '1rem 2rem 1rem 1rem' : '1rem 2rem',
        }}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex direction="row" alignItems="center" gap={10}>
          {selectedRule && (
            <Button
              icon="ArrowLeft"
              iconPlacement="left"
              onClick={() => setSelectedRule(null)}
              type="ghost"
              aria-label="Go back"
            />
          )}

          <Title level={3}>Explore data quality</Title>

          {selectedRule && (
            <StyledRuleHeader gap={10}>
              <Title level={4}>{selectedRule.name}</Title>
              {selectedRule.errorMessage && (
                <>
                  <Title level={4}>-</Title>
                  <Title level={4}>{selectedRule.errorMessage}</Title>
                </>
              )}
            </StyledRuleHeader>
          )}
        </Flex>
      </StyledRowHeader>

      {!selectedRule && (
        <Container>
          <StyledRulesTab>Rules</StyledRulesTab>
          {renderCollectionDetails()}
        </Container>
      )}

      {renderPageContent()}
    </StyledPage>
  );
};

const StyledRulesTab = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  width: 70px;
  padding: 10px;
  font-weight: 600;
  font-size: 1rem;
  border-bottom: 4px solid ${Colors['decorative--blue--500']};
`;

const StyledPage = styled(Flex)`
  flex: 1;
  overflow: hidden;
  background: var(--cogs-white);
`;

const StyledRowHeader = styled(Flex)`
  border-bottom: 1px solid var(--cogs-border-default);
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 100px auto;
  align-items: center;
  padding: 0 2rem;
  border-bottom: 1px solid var(--cogs-border-default);
`;

const StyledTabContainer = styled(Flex)`
  overflow: auto;
  background: var(--cogs-bg-accent);
  flex: 1;
`;

const StyledTabs = styled(Tabs)`
  .rc-tabs-nav {
    overflow: hidden;
  }
  .rc-tabs-tab {
    font-weight: 500;
    .rc-tabs-tab-btn {
      padding: 1.2rem 1rem;
    }
  }
  .rc-tabs-extra-content {
    flex: auto;
  }
  border-right: 1px solid var(--cogs-border-default);
`;

const StyledCollectionSummary = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 1rem;
  align-items: center;
  padding-left: 1rem;
  .collection-name {
    padding-right: 1rem;
    border-right: 1px solid var(--cogs-border-default);
  }
  .collection-statistics {
    margin-left: 1rem;
    max-width: 200px;
  }
`;

const StyledRuleHeader = styled(Flex)`
  padding-left: 1rem;
  border-left: 1px solid var(--cogs-border-default);
  max-width: 60vw;
  .cogs-title-4 {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
